<script lang="ts" setup>
import {useField} from "vee-validate";

const {specifications, parentSpecifications, childSpecifications} = useGlobalStore()
const model = defineModel();
const parentSpecificationId = ref<string | null>(null);
const childSpecificationId = ref<string | null>(null);

const props = defineProps({
    name: null,
    rules: null,
    onlySpecificationIds: {
        type: Array as PropType<String[]>,
    },
    selectedSpecification: {
        type: String,
    },
    disabled: {
        type: Boolean,
        default: false,
    }
});

const allowedParents = computed(() => {
    if (props.onlySpecificationIds?.length) {
        let parents = parentSpecifications.filter(item => props.onlySpecificationIds?.includes(item.documentId))
        if (!parents.length) {
            let childs = specifications.filter(item => props.onlySpecificationIds?.includes(item.documentId))
            if (childs.length) {
                parents = parentSpecifications.filter(item => childs.map(child => child.parent_id).includes(item.documentId))
            }
        }
        return parents
    }
    return parentSpecifications
})

const specificationChildList = computed(() => {
    if(parentSpecificationId.value){
        if (props.onlySpecificationIds?.length) {
            return childSpecifications(parentSpecificationId.value).filter(item => props.onlySpecificationIds?.includes(item.documentId))
        }
        return childSpecifications(parentSpecificationId.value)
    }
    return specifications
})

useField(props.name, props.rules, {
    type: 'default',
    syncVModel: true,
});

const selectedChildSpecification = computed(() => {
    return specificationChildList.value.find((item: any) => item.documentId === model.value);
})

const selectedParentSpecification = computed(() => {
    return allowedParents.value.find((item: any) => item.documentId === selectedChildSpecification?.value?.parent_id);
});

watch(() => parentSpecificationId.value, () => {
    if (specificationChildList.value.length === 1) {
        childSpecificationId.value = specificationChildList.value[0].documentId
        return
    }
    childSpecificationId.value = null;
})

function applyAction() {
    model.value = childSpecificationId.value;
}

function onClose() {
    childSpecificationId.value = null;
    parentSpecificationId.value = null;
}

const oneSpecialisation = computed(() => {
    if (props.onlySpecificationIds && props.onlySpecificationIds.length != 1) return
    return specifications.find(item => item.documentId === props.onlySpecificationIds?.[0])
})

const oneSpecialisationParent = computed(() => {
    if (!oneSpecialisation.value || !oneSpecialisation.value.parent_id) return
    return specifications.find(item => item.documentId === oneSpecialisation.value?.parent_id)
})

onMounted(() => {
    if (props.onlySpecificationIds && props.onlySpecificationIds.length === 1) {
        model.value = props.onlySpecificationIds[0];
        return
    }

    if (allowedParents.value.length === 1) {
        parentSpecificationId.value = allowedParents.value[0].documentId
    }
    if (specificationChildList.value.length === 1) {
        childSpecificationId.value = specificationChildList.value[0].documentId
    }
})

const isInitial = ref(false)
watch(model, (value) => {
    if(!isInitial.value) {
        console.log(value)
    }
}, {immediate: true})

</script>

<template>
    <div>
        <div v-if="!!oneSpecialisation">
            <div class="flex gap-2">
                <div
                        class="w-[48px] h-[48px] rounded-full mr-2 border text-gray-600 border-gray-200 flex justify-center items-center">
                    <Icon name="ri:briefcase-line" size="20"/>
                </div>
                <div>
                    <p class="text-[16px] font-medium">
                        <span v-if="oneSpecialisationParent">{{ oneSpecialisationParent?.name }}</span>
                        <span v-else>{{ $t("Select category") }}</span>
                    </p>
                    <p class="text-sm font-[400]">
                        <span v-if="oneSpecialisation">{{ oneSpecialisation?.name }}</span>
                        <span v-else>{{ $t("Specify the category of service") }}</span>
                    </p>
                </div>
            </div>
        </div>
        <UiDialog v-else>
            <UiDialogTrigger as-child>
                <custom-ui-card class="cursor-pointer bg-white">
                    <div class="flex gap-2">
                        <div
                                class="w-[48px] h-[48px] rounded-full mr-2 border text-gray-600 border-gray-200 flex justify-center items-center">
                            <Icon name="ri:briefcase-line" size="20"/>
                        </div>
                        <div>
                            <p class="text-[16px] font-medium">
                                <span v-if="selectedParentSpecification">{{ selectedParentSpecification?.name }}</span>
                                <span v-else>{{ $t("Select category") }}</span>
                            </p>
                            <p class="text-sm font-[400]">
                                <span v-if="selectedChildSpecification">{{ selectedChildSpecification?.name }}</span>
                                <span v-else>{{ $t("Specify the category of service") }}</span>
                            </p>
                        </div>
                        <Icon class="self-center ml-auto" name="ri:arrow-down-s-line" size="18"/>
                    </div>
                </custom-ui-card>
            </UiDialogTrigger>
            <UiDialogContent class="w-full md:w-auto ys-modal-dialog gap-0 border-0 md:min-w-[640px]">
                <div class="p-3 border-b border-b-gray-200 flex justify-between items-center">
                    <div class="flex items-center">
                        <Icon class="ys-role-icon-style mr-4" name="ri:add-fill" size="24"/>
                        <div class="grid text-start">
                            <p class="font-medium">{{ $t("Select category") }}</p>
                            <p class="text-xs font-normal text-gray-500">{{ $t("Specify the category of service") }}</p>
                        </div>
                    </div>
                    <UiDialogClose class="text-gray-500 self-start" @click="onClose">
                        <Icon name="ri:close-line" size="15"/>
                    </UiDialogClose>
                </div>
                <div class="px-5 md:px-0 py-4 sm:p-4 gap-2  text-center">
                    <div class="grid grid-cols-2 mb-2 text-[16px] text-left">
                        <div class="h-[310px] overflow-y-auto px-2 sm:pr-6 scrollbar">
                            <ul>
                                <li v-for="item in allowedParents"
                                    :key="item.documentId"
                                    :class="{ 'bg-gray-100': parentSpecificationId === item.documentId }"
                                    class="cursor-pointer text-gray-800 py-1 mb-0.5 px-2 font-[600] text-[12px] sm:text-[16px] hover:bg-gray-100 rounded-md"
                                    @click="parentSpecificationId = item.documentId">
                                    {{ item.name }}
                                </li>
                            </ul>
                        </div>
                        <div class="h-[310px] overflow-y-auto pl-2 sm:pl-6 scrollbar">
                            <ul>
                                <li v-for="item in specificationChildList"
                                    :key="item.documentId"
                                    :class="{ 'bg-gray-100': childSpecificationId === item.documentId }"
                                    class="cursor-pointer text-gray-800 py-1 mb-0.5 px-2 font-[400] text-[12px] sm:text-[16px] hover:bg-gray-100 rounded-md"
                                    @click="childSpecificationId = item.documentId">{{ item.name }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="px-4 py-5 grid grid-cols-2 gap-4 w-full border-t border-b-gray-200 mt-auto">
                    <UiDialogClose class="w-full">
                        <UiButton class="w-full" type="button" variant="secondary" @click="onClose">
                            {{ $t("Cancel") }}
                        </UiButton>
                    </UiDialogClose>
                    <UiDialogClose class="w-full">
                        <UiButton :disabled="childSpecificationId === null" class="w-full" variant="primary"
                                  @click="applyAction">
                            {{ $t("Select") }}
                        </UiButton>
                    </UiDialogClose>
                </div>
            </UiDialogContent>
        </UiDialog>
    </div>

</template>

<style lang="scss" scoped>

.ys-role-icon-style {
  width: 44px;
  height: 44px;
  padding: 10px;
  gap: 10px;
  border-radius: 96px;
  border: 1px solid rgba(226, 228, 233, 1);
  box-shadow: 0 1px 2px 0 rgba(228, 229, 231, 0.24);
  color: rgba(82, 88, 102, 1);
  background: rgba(255, 255, 255, 1);
}

.scrollbar {
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgb(250, 250, 250)
  }

  &::-webkit-scrollbar-thumb {
    background: rgb(45, 159, 117);
    border-radius: 999px;
    transition: all .4s;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #dbdcde;
  }
}

</style>